/**
 * Value (de)serializer for authored template cells.
 *
 * Stored shape must be EXACTLY what monday's API returns for column_values —
 * { id, value, text, type } with `value` a JSON string — because both apply paths
 * (App.js applyTemplate and spt ApplyTemplateJob.createSubitem) parse that shape:
 *   - status/color  → sends { label: text }
 *   - dropdown      → sends text directly (create_labels_if_missing)
 *   - everything else → JSON.parse(value)
 *
 * Getting a shape wrong fails SILENTLY at apply (per-row errors are swallowed and
 * logged), so this module is covered by fixture tests (columnValues.test.js) and
 * documented in implementation-plan.md §1.3. Do not change shapes casually.
 */

/**
 * Editor draft value conventions (input of serializeColumnValue), keyed by
 * CANONICAL type — monday's current names. Legacy aliases (color, multiple-person,
 * numeric, timerange, boolean, long-text) are accepted via Config.canonicalType,
 * because templates saved by older app versions stored those.
 *   status            { index: number, label: string }
 *   dropdown          { ids: number[], labels: string[] }   (ids may be empty)
 *   people            { persons: [{ id, name }] }
 *   date              'YYYY-MM-DD' | { dynamic: { base, offset, unit } }
 *   timeline          { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' } | { dynamic: { start, end } }
 *   numbers           number | numeric string
 *   text/long_text    string
 *   checkbox          true | false
 *   link              { url: string, text: string }
 *   rating            number (1..limit from the column's settings)
 *   file              { files: [{ fileId, name, size }] } — files uploaded through
 *                     lib/files; stored with value null (never sent to create_item,
 *                     the server's files stage copies them) and the files beside
 */

import { canonicalType } from '../Config';

const isEmpty = (v) =>
  v === undefined ||
  v === null ||
  v === '' ||
  (Array.isArray(v) && v.length === 0);

/**
 * A link cell's URL: a web address with a real host name ("example.com",
 * "https://example.com/a?b=1"). The https:// may be left off; spaces, other
 * schemes, a user:password@ part and hosts like "test_user," are refused.
 */
export function isValidLinkUrl(raw) {
  const s = String(raw == null ? '' : raw).trim();
  if (!s || /\s/.test(s)) return false;
  let u;
  try {
    u = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`);
  } catch (e) {
    return false;
  }
  if (u.username || u.password) return false;
  const label = '[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?';
  const host = new RegExp(`^(?:${label}\\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$`, 'i');
  return host.test(u.hostname) || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(u.hostname);
}

/**
 * @returns {{id, value, text, type}|null} null means "no value for this cell" —
 * the cell is simply absent from column_values, which apply treats as empty.
 */
export function serializeColumnValue(column, draft) {
  if (isEmpty(draft)) return null;
  // store the type exactly as the column reports it; branch on the canonical name
  const base = { id: column.id, type: column.type };

  switch (canonicalType(column.type)) {
    case 'status': {
      if (draft.index === undefined || draft.index === null) return null;
      return {
        ...base,
        value: JSON.stringify({ index: draft.index }),
        text: draft.label ?? '',
      };
    }
    case 'dropdown': {
      const labels = draft.labels ?? [];
      if (!labels.length) return null;
      return {
        ...base,
        value: JSON.stringify({ ids: draft.ids ?? [] }),
        text: labels.join(', '), // apply sends text; monday accepts "A, B" + create_labels_if_missing
      };
    }
    case 'people': {
      const persons = draft.persons ?? [];
      if (!persons.length) return null;
      return {
        ...base,
        value: JSON.stringify({
          personsAndTeams: persons.map((p) => ({ id: Number(p.id), kind: 'person' })),
        }),
        text: persons.map((p) => p.name).join(', '),
      };
    }
    case 'date':
      // A rule instead of a date (lib/dynamicValues): kept beside the usual shape
      // with value null, and resolved to a real date by the apply pre-pass.
      if (typeof draft === 'object' && draft.dynamic) {
        return { ...base, value: null, text: '', dynamic: { ...draft.dynamic } };
      }
      return { ...base, value: JSON.stringify({ date: draft }), text: draft };
    case 'timeline': {
      // a rule for each end (lib/dynamicValues), resolved like a date rule
      if (draft.dynamic) {
        return { ...base, value: null, text: '', dynamic: { start: { ...draft.dynamic.start }, end: { ...draft.dynamic.end } } };
      }
      if (!draft.from || !draft.to) return null;
      return {
        ...base,
        value: JSON.stringify({ from: draft.from, to: draft.to }),
        text: `${draft.from} - ${draft.to}`,
      };
    }
    case 'numbers': {
      const s = String(draft);
      return { ...base, value: JSON.stringify(s), text: s };
    }
    case 'rating': {
      const n = Number(draft);
      if (!n || n <= 0) return null; // 0 / empty = no rating
      // accepted + returned per-column shape is { rating: n }
      return { ...base, value: JSON.stringify({ rating: n }), text: String(n) };
    }
    case 'checkbox': {
      if (!draft) return null; // unchecked = no value, matches monday
      return { ...base, value: JSON.stringify({ checked: 'true' }), text: 'v' };
    }
    case 'link': {
      if (!draft.url) return null;
      return {
        ...base,
        value: JSON.stringify({ url: draft.url, text: draft.text || draft.url }),
        text: `${draft.text || draft.url} - ${draft.url}`,
      };
    }
    case 'file': {
      const files = (draft.files || []).filter((f) => f && Number(f.fileId) > 0);
      if (!files.length) return null;
      return {
        ...base,
        value: null,
        text: files.map((f) => f.name || '').join(', '),
        files: files.map((f) => ({ fileId: Number(f.fileId), name: String(f.name || ''), size: Number(f.size) || 0 })),
      };
    }
    case 'text':
    case 'long_text':
    default: {
      const s = String(draft);
      return { ...base, value: JSON.stringify(s), text: s };
    }
  }
}

/** Inverse: stored column_value → editor draft value (null = empty cell). */
export function parseColumnValue(cv, column) {
  // a dynamic rule (date or timeline) has no value yet; the draft carries the rule itself
  if (cv && cv.dynamic && ['date', 'timeline'].includes(canonicalType(column.type))) {
    return { dynamic: { ...cv.dynamic } };
  }
  // a file cell has no value either; its files ride beside it. A file cell
  // captured from monday (value = monday's asset list, no files) is empty.
  if (cv && canonicalType(column.type) === 'file') {
    const files = (cv.files || []).filter((f) => f && Number(f.fileId) > 0);
    return files.length ? { files: files.map((f) => ({ fileId: Number(f.fileId), name: f.name || '', size: Number(f.size) || 0 })) } : null;
  }
  if (!cv || cv.value === null || cv.value === undefined || cv.value === '') {
    return null;
  }
  let v;
  try {
    v = JSON.parse(cv.value);
  } catch (e) {
    v = cv.value;
  }
  if (v === null) return null;

  switch (canonicalType(column.type)) {
    case 'status':
      return v.index === undefined ? null : { index: v.index, label: cv.text ?? '' };
    case 'dropdown': {
      const labels = (cv.text ?? '').split(', ').filter(Boolean);
      return labels.length ? { ids: v.ids ?? [], labels } : null;
    }
    case 'people': {
      const persons = (v.personsAndTeams ?? [])
        .filter((p) => p.kind === 'person')
        .map((p) => ({ id: p.id, name: '' }));
      // names come from cv.text ("A, B") in the same order monday emits them
      const names = (cv.text ?? '').split(', ').filter(Boolean);
      persons.forEach((p, i) => {
        p.name = names[i] ?? '';
      });
      return persons.length ? { persons } : null;
    }
    case 'date':
      return v.date ?? null;
    case 'timeline':
      return v.from && v.to ? { from: v.from, to: v.to } : null;
    case 'numbers':
      return typeof v === 'string' || typeof v === 'number' ? String(v) : null;
    case 'rating':
      return v.rating ? Number(v.rating) : null;
    case 'checkbox':
      return v.checked === 'true' || v.checked === true;
    case 'link':
      return v.url ? { url: v.url, text: v.text ?? '' } : null;
    case 'text':
    case 'long_text':
    default:
      return typeof v === 'string' ? v : cv.text ?? null;
  }
}

/** Build the column_values array for one authored row (drops empty cells). */
export function serializeRowValues(columns, draftValues) {
  const out = [];
  for (const col of columns) {
    const cv = serializeColumnValue(col, draftValues[col.id]);
    if (cv) out.push(cv);
  }
  return out;
}
